import React, { useState, useEffect } from "react"
import { IoIosArrowRoundBack } from "react-icons/io"
import { useHistory } from "react-router-dom"
import { FiSearch } from "react-icons/fi"
import axios from "axios"
import { SuccessNotification, ErrorNotification } from "../../utils"
import Spinner from "../Spinner"
import api, { BASE_URL } from "../../utils/api"
import { FaRegTrashAlt } from "react-icons/fa"
import Pagination from "../Pagination"
import useDeBounce from "../../utils/useDeBounce"
import {
  PAGE_SIZE,
  mapPotentialMember,
  getMemberTypeForApi,
} from "../../utils/groupMemberUtils"
import { parseApiFormErrors, getFirstFormError } from "../../utils/formErrors"

const GROUP_API_FIELD_MAP = {
  group_name: "name",
  group_type: "groupType",
  description: "description",
  initial_members: "members",
}

const groupTypes = [
  { value: "farmers", label: "Farmers" },
  { value: "processors", label: "Processors" },
]

export default function NewGroupForm({ requireMembers = false }) {
  const history = useHistory()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [groupType, setGroupType] = useState("")
  const [searchText, setSearchText] = useState("")
  const debouncedSearch = useDeBounce(searchText, 400)
  const [members, setMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedMembers, setSelectedMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const selectedIds = new Set(selectedMembers.map((m) => m.value))

  useEffect(() => {
    setSelectedMembers([])
    setSearchText("")
    setCurrentPage(1)
  }, [groupType])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    if (!groupType) {
      setMembers([])
      setTotalCount(0)
      return
    }

    const fetchMembers = async () => {
      setMembersLoading(true)
      const token = localStorage.getItem("token")

      try {
        if (groupType === "farmers") {
          const resp = await api.get("farmers/", {
            params: {
              page: currentPage,
              page_size: PAGE_SIZE,
              search: debouncedSearch || undefined,
            },
          })
          const results = (resp.data.results || []).map((f) =>
            mapPotentialMember({
              id: f.id,
              firstname: f.firstname,
              lastname: f.lastname,
              member_type: "farmer",
            })
          )
          setMembers(results)
          setTotalCount(resp.data.count || results.length)
        } else {
          let url = `${BASE_URL}groups/potential_members/?group_type=${groupType}`
          if (debouncedSearch) {
            url += `&search=${encodeURIComponent(debouncedSearch)}`
          }
          const resp = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
          const allMembers = (resp.data.members || []).map(mapPotentialMember)
          const start = (currentPage - 1) * PAGE_SIZE
          setMembers(allMembers.slice(start, start + PAGE_SIZE))
          setTotalCount(allMembers.length)
        }
      } catch {
        setMembers([])
        setTotalCount(0)
      } finally {
        setMembersLoading(false)
      }
    }

    fetchMembers()
  }, [groupType, debouncedSearch, currentPage])

  const toggleMember = (member) => {
    clearFieldError("members")
    if (selectedIds.has(member.value)) {
      setSelectedMembers(selectedMembers.filter((m) => m.value !== member.value))
    } else {
      setSelectedMembers([...selectedMembers, member])
    }
  }

  const removeMember = (value) => {
    setSelectedMembers(selectedMembers.filter((m) => m.value !== value))
  }

  const clearAll = () => setSelectedMembers([])

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const validate = () => {
    const errs = {}
    if (!name.trim()) errs.name = "Group name is required"
    if (!description.trim()) errs.description = "Group description is required"
    if (!groupType) errs.groupType = "Group type is required"
    if (requireMembers && selectedMembers.length === 0) {
      errs.members = "At least one member is required"
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const createGroup = (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    const initial_members = selectedMembers.map((m) => ({
      member_type: getMemberTypeForApi(groupType),
      member_id: m.member_id,
    }))
    const data = JSON.stringify({
      group_name: name,
      group_type: groupType,
      description,
      initial_members,
    })
    const token = localStorage.getItem("token")
    axios
      .post(`${BASE_URL}groups/`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setLoading(false)
        setDescription("")
        setName("")
        setSelectedMembers([])
        setGroupType("")
        SuccessNotification(res.data.message)
        history.goBack()
      })
      .catch((error) => {
        setLoading(false)
        const { fieldErrors, generalError } = parseApiFormErrors(
          error?.response?.data,
          GROUP_API_FIELD_MAP
        )

        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }))
        }

        ErrorNotification(
          generalError ||
            getFirstFormError(fieldErrors) ||
            "An error occurred when creating group"
        )
      })
  }

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10">
      <div className="w-full">
        <button onClick={() => history.goBack()} className="mb-5 flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">Back</p>
        </button>
        <h2 className="text-2xl font-bold mb-6 dark:text-white">Create New Group</h2>

        <form className="bg-white dark:bg-dark rounded-lg border dark:border-border-dark" onSubmit={createGroup}>
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x dark:divide-border-dark">
            {/* Left: form + selected members */}
            <div className="px-3 py-4 md:px-8 md:py-8 flex flex-col gap-6">
              <div>
                <h3 className="text-yellowcolor text-lg font-semibold mb-4">Group Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">
                      Group Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      placeholder="Enter group name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value)
                        clearFieldError("name")
                      }}
                    />
                    {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">
                      Group Type<span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                      value={groupType}
                      onChange={(e) => {
                        setGroupType(e.target.value)
                        clearFieldError("groupType")
                      }}
                    >
                      <option value="">Select group type</option>
                      {groupTypes.map((gt) => (
                        <option key={gt.value} value={gt.value}>
                          {gt.label}
                        </option>
                      ))}
                    </select>
                    {errors.groupType && <div className="text-xs text-red-500 mt-1">{errors.groupType}</div>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">
                      Group Description<span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      placeholder="Created this group for easy access to send requests"
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value)
                        clearFieldError("description")
                      }}
                    />
                    {errors.description && <div className="text-xs text-red-500 mt-1">{errors.description}</div>}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-yellowcolor text-lg font-semibold">
                    Selected Members
                    {selectedMembers.length > 0 && (
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({selectedMembers.length})
                      </span>
                    )}
                  </h3>
                  {selectedMembers.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="flex items-center text-red-500 text-sm hover:underline"
                    >
                      Clear all <FaRegTrashAlt className="ml-1" />
                    </button>
                  )}
                </div>
                {errors.members && <div className="text-xs text-red-500 mb-2">{errors.members}</div>}
                {selectedMembers.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center border border-dashed dark:border-border-dark rounded-lg">
                    {groupType
                      ? "Select members from the list on the right"
                      : "Choose a group type to start selecting members"}
                  </p>
                ) : (
                  <ul className="max-h-52 overflow-y-auto space-y-2 border dark:border-border-dark rounded-lg p-2">
                    {selectedMembers.map((member) => (
                      <li
                        key={member.value}
                        className="flex items-center justify-between bg-gray-50 dark:bg-secondaryDark rounded-md px-3 py-2 text-sm dark:text-white"
                      >
                        <span>{member.label}</span>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-500 ml-2 shrink-0"
                          onClick={() => removeMember(member.value)}
                          aria-label={`Remove ${member.label}`}
                        >
                          &times;
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Right: searchable paginated member list */}
            <div className="flex flex-col min-h-[400px] lg:min-h-[500px]">
              <div className="px-3 py-4 md:px-8 md:py-6 border-b dark:border-border-dark">
                <h3 className="text-yellowcolor text-lg font-semibold mb-4">
                  {groupType === "processors" ? "Available Processors" : "Available Farmers"}
                </h3>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    className="w-full pl-10 bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 disabled:opacity-50"
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    disabled={!groupType}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 md:px-8">
                {!groupType ? (
                  <div className="flex items-center justify-center h-full py-16 text-gray-400 dark:text-gray-500 text-sm text-center px-4">
                    Select a group type to browse members
                  </div>
                ) : membersLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Spinner />
                  </div>
                ) : members.length === 0 ? (
                  <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500 text-sm">
                    No members found
                  </div>
                ) : (
                  <ul className="divide-y dark:divide-border-dark">
                    {members.map((member) => {
                      const isSelected = selectedIds.has(member.value)
                      return (
                        <li key={member.value}>
                          <label className="flex items-center gap-3 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondaryDark rounded-md px-2 -mx-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleMember(member)}
                              className="h-4 w-4 rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor"
                            />
                            <span className="text-sm dark:text-white">{member.label}</span>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>

              {groupType && totalCount > PAGE_SIZE && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          </div>

          <div className="flex border-t border-gray-200 dark:border-border-dark p-4 flex-row justify-end gap-4">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="px-6 text-sm py-2 dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg font-semibold hover:bg-yellow-400 flex items-center gap-2"
              disabled={loading}
            >
              {loading ? "Please wait..." : "Create Group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
